import { useId, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { claimDates, decodeJwt } from "../lib/jwt";

const SAMPLE_JWT =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c";

const paneClass =
  "min-h-[160px] w-full rounded-md border border-border bg-card px-3 py-2 font-mono text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        if (typeof navigator === "undefined") return;
        navigator.clipboard?.writeText(value).then(
          () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          },
          () => {},
        );
      }}
      className="rounded-md border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
    >
      {copied ? m.jw_copied() : m.jw_copy()}
    </button>
  );
}

export function JwtTool() {
  const uid = useId();
  const [token, setToken] = useState(SAMPLE_JWT);

  const result = useMemo(() => decodeJwt(token), [token]);
  const headerText = result.ok ? JSON.stringify(result.header, null, 2) : "";
  const payloadText = result.ok ? JSON.stringify(result.payload, null, 2) : "";
  const claims = result.ok ? claimDates(result.payload) : [];

  return (
    <div className="space-y-6">
      <div
        role="note"
        className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
      >
        {m.jw_noverify_notice()}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${uid}-token`}>{m.jw_input_label()}</Label>
        <textarea
          id={`${uid}-token`}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          spellCheck={false}
          className="min-h-[110px] w-full break-all rounded-md border border-border bg-card px-3 py-2 font-mono text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      {result.ok ? (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor={`${uid}-header`}>{m.jw_header_label()}</Label>
                <CopyButton value={headerText} />
              </div>
              <textarea
                id={`${uid}-header`}
                data-testid="jwt-header"
                readOnly
                value={headerText}
                className={paneClass}
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor={`${uid}-payload`}>{m.jw_payload_label()}</Label>
                <CopyButton value={payloadText} />
              </div>
              <textarea
                id={`${uid}-payload`}
                data-testid="jwt-payload"
                readOnly
                value={payloadText}
                className={paneClass}
              />
            </div>
          </div>

          {claims.length > 0 && (
            <div
              data-testid="jwt-claims"
              className="rounded-2xl border border-border bg-card/60 p-4 sm:p-6"
            >
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {m.jw_claims_title()}
              </h2>
              <dl className="space-y-2">
                {claims.map((c) => (
                  <div
                    key={c.claim}
                    className="flex items-baseline justify-between gap-3 text-sm"
                  >
                    <dt className="font-mono text-muted-foreground">
                      {c.claim}
                    </dt>
                    <dd className="font-mono text-foreground">{c.iso}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </>
      ) : (
        <p data-testid="jwt-error" className="text-sm text-destructive">
          {result.error}
        </p>
      )}
    </div>
  );
}
